// src/auth.js
// src/AuthProvider.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { loadSubscription } from "./api/users";
import { endSupportAccess, loadSupportStatus, startSupportAccess } from "./api/support";

// ✅ Use ONE key across the app (matches src/api/users.js)
const SUB_KEY = "sb_user_subscription_v1";
const SUPPORT_KEY = "sb_support_account_v1";

function readSupportAccount() {
  try { return JSON.parse(sessionStorage.getItem(SUPPORT_KEY) || "null"); } catch { return null; }
}

function writeSupportAccount(account) {
  try {
    if (!account) sessionStorage.removeItem(SUPPORT_KEY);
    else sessionStorage.setItem(SUPPORT_KEY, JSON.stringify(account));
  } catch {}
}

function readSubCache() {
  try {
    const raw = sessionStorage.getItem(SUB_KEY);
    return raw ? JSON.parse(raw) : null; // ✅ inner subscription object
  } catch {
    return null;
  }
}

function writeSubCache(sub) {
  try {
    if (!sub) sessionStorage.removeItem(SUB_KEY);
    else sessionStorage.setItem(SUB_KEY, JSON.stringify(sub)); // ✅ inner subscription object
  } catch {}
}

// Normalize subscription.account.imageDir into a safe ImageKit folder
function normalizeImagekitFolder(imageDir) {
  let folder = (imageDir || "").trim();
  if (!folder) return "/uploads";
  if (!folder.startsWith("/")) folder = `/${folder}`;
  if (folder.length > 1) folder = folder.replace(/\/+$/, "");
  folder = folder.replace(/[^a-zA-Z0-9\/._-]/g, "-");
  folder = folder.replace(/\/{2,}/g, "/");
  return folder;
}

// Normalize post kinds from subscription into [{value,label}]
function normalizePostKinds(subscription) {
  const raw = subscription?.plan?.postKinds || {};

  return Object.entries(raw)
    .filter(([_, v]) => v?.enabled !== false)
    .map(([key, v]) => ({
      value: key,
      label: key,
      enabled: v?.enabled,
      limit: v?.limit,
    }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

const Ctx = createContext({
  user: null,
  claims: null,
  subscription: null, // ✅ inner subscription object: { plan, account }

  imageDir: "",
  imagekitFolder: "/uploads",
  postKindOptions: [],

  loading: true,
  subscriptionLoading: false,
  refreshSubscription: async () => null,
  logout: async () => {},
});

export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);

  // ✅ store the *inner* subscription in state (not a wrapper package)
  const [subscription, setSubscription] = useState(() => readSubCache() || null);
  const [supportAccount, setSupportAccount] = useState(() => readSupportAccount());
  const [supportAvailable, setSupportAvailable] = useState(false);

  const [loading, setLoading] = useState(true);

  // ✅ if no cache, we consider subscription "loading" until we fetch it
  const [subscriptionLoading, setSubscriptionLoading] = useState(() => {
    const cached = readSubCache();
    return !cached;
  });

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);

      if (!u) {
        setClaims(null);
        setSubscription(null);
        writeSubCache(null);
        writeSupportAccount(null);
        setSupportAccount(null);
        setSupportAvailable(false);
        setSubscriptionLoading(false);
        setLoading(false);
        return;
      }

      const token = await u.getIdTokenResult(true); // force refresh so custom claims are fresh
      setClaims(token.claims || null);
      loadSupportStatus()
        .then(() => setSupportAvailable(true))
        .catch(() => setSupportAvailable(false));

      const cachedSub = readSubCache();

      if (!cachedSub) {
        setSubscriptionLoading(true);
        try {
          // ✅ loadSubscription() reads auth.currentUser internally
          const sub = await loadSubscription();
          writeSubCache(sub);
          setSubscription(sub || null);
        } catch (e) {
          console.error("Failed to load subscription:", e?.message || e);
          writeSubCache(null);
          setSubscription(null);
        } finally {
          setSubscriptionLoading(false);
        }
      } else {
        setSubscription(cachedSub);
        setSubscriptionLoading(false);
      }

      setLoading(false);
    });
  }, []);

  const refreshSubscription = async () => {
    const u = auth.currentUser;
    if (!u) return null;

    setSubscriptionLoading(true);
    try {
      const sub = await loadSubscription();
      writeSubCache(sub);
      setSubscription(sub || null);
      return sub || null;
    } catch (e) {
      console.error("refreshSubscription failed:", e?.message || e);
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const logout = async () => {
    writeSubCache(null);
    writeSupportAccount(null);
    await signOut(auth);
  };

  const beginSupport = async (accountId) => {
    const response = await startSupportAccess(accountId);
    const account = response?.result?.account;
    if (!account?.id) throw new Error("Support account was not returned");
    writeSupportAccount(account);
    setSupportAccount(account);
    writeSubCache(null);
    const sub = await loadSubscription();
    writeSubCache(sub);
    setSubscription(sub || null);
    return account;
  };

  const endSupport = async () => {
    const account = readSupportAccount();
    if (account?.id) await endSupportAccess(account.id).catch(() => null);
    writeSupportAccount(null);
    setSupportAccount(null);
    writeSubCache(null);
    const sub = await loadSubscription();
    writeSubCache(sub);
    setSubscription(sub || null);
  };

  const imageDir = subscription?.account?.imageDir || "";
  const imagekitFolder = useMemo(() => normalizeImagekitFolder(imageDir), [imageDir]);
  const postKindOptions = useMemo(() => normalizePostKinds(subscription), [subscription]);

  return (
    <Ctx.Provider
      value={{
        user,
        claims,
        subscription,
        supportAccount,
        supportAvailable,
        beginSupport,
        endSupport,

        imageDir,
        imagekitFolder,
        postKindOptions,

        loading,
        subscriptionLoading,
        refreshSubscription,
        logout,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}
